'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Download, FileText, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatFileSize } from '@kit/shared/lib/file-utilities';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Skeleton } from '@kit/ui/skeleton';
import { Trans } from '@kit/ui/trans';

import { useDocumentDownload } from '../hooks/use-document-download';
import { getDocumentSignedUrlAction } from '../server/actions';
import type { Document } from '../types';
import { formatDocumentDateTime } from '../utils/date-utils';
import { isImageFile, isPdfFile } from '../utils/file-type-utils';
import { DocumentNavigator } from './document-navigator';
import { ImageViewer } from './image-viewer';
import { PdfViewer } from './pdf-viewer';

interface DocumentViewerDialogProps {
  document: Document | null;
  allDocuments?: Document[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete?: () => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  showDeleteAction?: boolean;
  currentIndex?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetZoom?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

/**
 * Document viewer dialog with keyboard shortcuts
 *
 * Keyboard shortcuts:
 * - ESC: Close dialog
 * - Arrow Left/Right: Navigate between documents
 * - +/-: Zoom in/out (images only)
 * - 0: Reset zoom
 */
export function DocumentViewerDialog({
  document,
  allDocuments = [],
  open,
  onOpenChange,
  onDelete,
  showDeleteAction = false,
  currentIndex = 0,
  onNavigate,
  hasPrevious = false,
  hasNext = false,
  zoom = 100,
  onZoomChange,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  isFullscreen = false,
  onToggleFullscreen,
}: DocumentViewerDialogProps) {
  const { t } = useTranslation();
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { download, isDownloading } = useDocumentDownload();

  const isImage = document ? isImageFile(document.mimeType) : false;
  const isPdf = document ? isPdfFile(document.mimeType) : false;
  const canNavigate = allDocuments.length > 1 && onNavigate;

  const handleZoomChange = useMemo(
    () => onZoomChange ?? (() => {}),
    [onZoomChange],
  );
  const handleZoomIn = useMemo(
    () => onZoomIn ?? (() => handleZoomChange(Math.min(zoom + 25, 200))),
    [onZoomIn, handleZoomChange, zoom],
  );
  const handleZoomOut = useMemo(
    () => onZoomOut ?? (() => handleZoomChange(Math.max(zoom - 25, 50))),
    [onZoomOut, handleZoomChange, zoom],
  );
  const handleResetZoom = useMemo(
    () => onResetZoom ?? (() => handleZoomChange(100)),
    [onResetZoom, handleZoomChange],
  );
  const handleToggleFullscreen = useMemo(
    () => onToggleFullscreen ?? (() => {}),
    [onToggleFullscreen],
  );

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case 'Escape':
          onOpenChange(false);
          break;
        case 'ArrowLeft':
          if (canNavigate && hasPrevious) {
            e.preventDefault();
            onNavigate('prev');
          }
          break;
        case 'ArrowRight':
          if (canNavigate && hasNext) {
            e.preventDefault();
            onNavigate('next');
          }
          break;
        case '+':
        case '=':
          if (isImage) {
            e.preventDefault();
            handleZoomIn();
          }
          break;
        case '-':
          if (isImage) {
            e.preventDefault();
            handleZoomOut();
          }
          break;
        case '0':
          if (isImage) {
            e.preventDefault();
            handleResetZoom();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    open,
    onOpenChange,
    canNavigate,
    hasPrevious,
    hasNext,
    onNavigate,
    isImage,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
  ]);

  // Fetch document URL (skip if pre-fetched signedUrl is available)
  const fetchDocumentUrl = useCallback(async () => {
    if (!document) return;

    // Use pre-fetched signed URL if available
    if (document.signedUrl) {
      setDocumentUrl(document.signedUrl);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setDocumentUrl(null);

    try {
      const result = await getDocumentSignedUrlAction({
        bucket: document.bucket,
        path: document.storagePath,
      });

      if (result.success) {
        setDocumentUrl(result.url);
      } else {
        setError(result.error || t('documents:viewer.failedToLoad'));
      }
    } catch {
      setError(t('documents:viewer.loadFailedRetry'));
    } finally {
      setLoading(false);
    }
  }, [document, t]);

  useEffect(() => {
    if (!open || !document) return;
    // Intentional: fast path syncs cached signedUrl into state; async path
    // fetches via server action. Refactoring to derived state would lose
    // the loading/error states needed for the async fetch.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchDocumentUrl();
  }, [document, open, fetchDocumentUrl]);

  const handleDownload = useCallback(async () => {
    if (document) {
      await download(document);
    }
  }, [document, download]);

  const handleImageError = useCallback(() => {
    setError(t('documents:viewer.loadImageFailed'));
  }, [t]);

  if (!document) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] flex-col p-0 sm:max-w-5xl">
        <DialogHeader className="border-b px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
                <FileText className="h-5 w-5" />
                {document.fileName}
              </DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-2">
                <span className="text-sm">
                  {formatFileSize(document.fileSize)}
                </span>
                {document.uploadedAt && (
                  <span className="text-sm">
                    <Trans
                      i18nKey="documents:card.uploaded"
                      values={{
                        date: formatDocumentDateTime(document.uploadedAt),
                      }}
                    />
                  </span>
                )}
              </DialogDescription>
              {document.description && (
                <p className="text-muted-foreground text-sm">
                  {document.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                <Download className="mr-1 h-4 w-4" />
                {isDownloading ? (
                  <Trans i18nKey="documents:card.downloading" />
                ) : (
                  <Trans i18nKey="documents:card.download" />
                )}
              </Button>
              {showDeleteAction && onDelete && (
                <Button variant="destructive" size="sm" onClick={onDelete}>
                  <Trash2 className="mr-1 h-4 w-4" />
                  <Trans i18nKey="documents:card.delete" />
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        {canNavigate && (
          <DocumentNavigator
            currentIndex={currentIndex}
            totalDocuments={allDocuments.length}
            onNavigate={onNavigate}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
          />
        )}

        <div className="flex-1 overflow-hidden">
          {loading && (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <Skeleton className="h-64 w-64 rounded-lg" />
              <p className="text-muted-foreground text-sm">
                <Trans i18nKey="documents:viewer.loadingDocument" />
              </p>
            </div>
          )}

          {error && (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
              <AlertCircle className="text-destructive h-12 w-12" />
              <div className="text-center">
                <h3 className="mb-2 text-lg font-semibold">
                  <Trans i18nKey="documents:viewer.failedToLoad" />
                </h3>
                <p className="text-muted-foreground text-sm">{error}</p>
              </div>
              <Button onClick={fetchDocumentUrl}>
                <Trans i18nKey="documents:actions.retry" />
              </Button>
            </div>
          )}

          {!loading && !error && documentUrl && (
            <>
              {isImage && (
                <ImageViewer
                  url={documentUrl}
                  fileName={document.fileName}
                  zoom={zoom}
                  onZoomChange={handleZoomChange}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onResetZoom={handleResetZoom}
                  isFullscreen={isFullscreen}
                  onToggleFullscreen={handleToggleFullscreen}
                  onError={handleImageError}
                />
              )}
              {isPdf && (
                <PdfViewer url={documentUrl} fileName={document.fileName} />
              )}
              {!isImage && !isPdf && (
                <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
                  <FileText className="text-muted-foreground h-12 w-12" />
                  <div className="text-center">
                    <h3 className="mb-2 text-lg font-semibold">
                      <Trans i18nKey="documents:viewer.previewNotAvailable" />
                    </h3>
                    <p className="text-muted-foreground mb-4 text-sm">
                      <Trans i18nKey="documents:viewer.downloadToView" />
                    </p>
                    <Button onClick={handleDownload} disabled={isDownloading}>
                      <Download className="mr-2 h-4 w-4" />
                      <Trans i18nKey="documents:actions.downloadFile" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Keyboard shortcuts help */}
        <div className="bg-muted/30 text-muted-foreground border-t px-4 py-2 text-center text-xs">
          <span>
            <Trans i18nKey="documents:viewer.shortcutsLabel" />{' '}
          </span>
          {/* ESC is a keyboard key cap label, not translatable copy */}
          <kbd className="bg-muted rounded px-1 py-0.5">{'ESC'}</kbd>{' '}
          <Trans i18nKey="documents:viewer.shortcutClose" />
          {canNavigate && (
            <>
              {' • '}
              <kbd className="bg-muted rounded px-1 py-0.5">←</kbd>
              <kbd className="bg-muted rounded px-1 py-0.5">→</kbd>{' '}
              <Trans i18nKey="documents:viewer.shortcutNavigate" />
            </>
          )}
          {isImage && (
            <>
              {' • '}
              <kbd className="bg-muted rounded px-1 py-0.5">+</kbd>
              <kbd className="bg-muted rounded px-1 py-0.5">-</kbd>{' '}
              <Trans i18nKey="documents:viewer.shortcutZoom" />
              {' • '}
              <kbd className="bg-muted rounded px-1 py-0.5">0</kbd>{' '}
              <Trans i18nKey="documents:viewer.shortcutReset" />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
